import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import { localeNumber } from "../../hooks/formatNumber";

const metricTypes: any = {
	webpageviews: "Web page views",
	apppageviews: "App page views",
	pageviews: "Page views",
	videoPageViews: "Streamstarts",
	conversion: "Conversions",
	totalReadingTime: "Total reading time",
	totalVisitors: "Total visitors",
	appVisitors: "App visitors",
	webVisitors: "Web visitors",
};
const colors = ["#652bff", "#cfc5f4"];
// const timeGreenColors = ["#60d470", "#d3ead5"];
const timeNeutralColors = ["#82d1e6", "#b5e1fd"];
// const timeRedColors = [];
const width = 450;
const height = width;
const radius = width / 2;
const thickness = radius * 0.25;
const innerWidth = width - 2 * thickness + 2;
const innerHeight = innerWidth;
const innerRadius = innerWidth / 2;
const innerThickness = thickness;

export default function GoalDonut() {
	const [initialLoad, setInitialLoad] = useState(true);
	const isLoading = useSelector((state: any) => state.goal.isLoading);
	useEffect(() => {
		if (!isLoading) setInitialLoad(false);
	}, [isLoading]);
	const chartRef = useRef(null);
	const innerChartRef = useRef(null);
	const data = useSelector((state: any) => state.goal.data);
	const { achieved, target, iterationStartDate, iterationEndDate, displayTimeleft, recurrencyType } = data;
	const brandsettings = useSelector((state: any) => state.brandsettings.data);

	const timezone =
		brandsettings && brandsettings.domainSettings && brandsettings.domainSettings.timezone
			? brandsettings.domainSettings.timezone
			: moment.tz.guess();
	const [showInner] = useState(true);
	const donePercent = useMemo(() => Math.min((achieved / target) * 100, 100), [achieved, target]);
	const progress = useMemo(
		() => [
			{ name: "done", value: donePercent },
			{ name: "remaining", value: 100 - donePercent },
		],
		[donePercent]
	);
	const deg = donePercent * 3.6;
	const iterationStartDateObj = moment.tz(iterationStartDate, timezone);
	const timeOffset = iterationStartDateObj.unix();
	
	// new Date(iterationStartDate).getTime();
	const timeTarget = moment.tz(iterationEndDate, timezone).unix() - timeOffset;
	
	// new Date(iterationEndDate).getTime() - timeOffset;
	const timeAchieved = moment.tz().unix() - timeOffset;
	const timePercent = useMemo(() => Math.min((timeAchieved / timeTarget) * 100, 100), [timeAchieved, timeTarget]);
	const timeProgress = useMemo(
		() => [
			{ name: "done", value: timePercent },
			{ name: "remaining", value: 100 - timePercent },
		],
		[timePercent]
	);
	const timeDeg = timePercent * 3.6;
	const timeLeft = timeTarget - timeAchieved;

	const timeLeftString = useMemo(() => {
		if (recurrencyType !== 'onetime' && iterationStartDateObj.isAfter(moment.tz())) {
			const startsIn = moment.duration(Math.abs(timeAchieved), "seconds");
			return `Starts in:<br/><span>${startsIn.days()}</span>d <span>${startsIn.hours()}</span>h <span>${startsIn.minutes()}</span>m`;
		}
		if (timeLeft < 0) return "Goal ended";
		if (!displayTimeleft) return "";
		const duration = moment.duration(timeLeft, "seconds");
		if (duration.days()) {
			return `Time left:<br/><span>${duration.days()}</span>d <span>${duration.hours()}</span>h <span>${duration.minutes()}</span>m`;
		} 
		return `Time left:<br/><span>${duration.hours()}</span>h <span>${duration.minutes()}</span>m`;
	}, [timeLeft, displayTimeleft, recurrencyType]);

	const drawOuterChart = useCallback(() => {
		// Remove old svg, if present
		d3.select(chartRef.current).selectAll("svg#outer").remove();
		// Create SVG
		const svg = d3
			.select(chartRef.current)
			.append("svg")
			.attr("width", "100%")
			.attr("height", "100%")
			.attr("viewBox", "0 0 " + width + " " + width)
			.attr("id", "outer")
			//.attr('preserveAspectRatio','xMinYMin')
			.append("g")
			.attr("transform", `translate(${Math.min(width, height) / 2},${Math.min(width, height) / 2})`);

		let pie = d3
			.pie()
			.sort(null)
			.value((d: any) => d.value);
		let data_ready = pie(progress as any);

		// Donut partition
		svg.selectAll("whatever")
			.data(data_ready)
			.enter()
			.append("path")
			.attr(
				"d",
				d3
					.arc()
					.innerRadius(radius - thickness) // This is the size of the donut hole
					.outerRadius(radius) as any
			)
			.attr("fill", (d) => colors[d.index]);
	}, [progress]);

	const drawInnerChart = useCallback(() => {
		// Remove old svg, if present
		d3.select(innerChartRef.current).selectAll("svg#inner").remove();
		// Create SVG
		const svg = d3
			.select(innerChartRef.current)
			.append("svg")
			.attr("width", "100%")
			.attr("height", "100%")
			.attr("viewBox", "0 0 " + innerWidth + " " + innerWidth)
			.attr("id", "inner")
			//.attr('preserveAspectRatio','xMinYMin')
			.append("g")
			.attr(
				"transform",
				`translate(${Math.min(innerWidth, innerHeight) / 2},${Math.min(innerWidth, innerHeight) / 2})`
			);

		let pie = d3
			.pie()
			.sort(null)
			.value((d: any) => d.value);
		let data_ready = pie(timeProgress as any);

		// Donut partition
		svg.selectAll("whatever")
			.data(data_ready)
			.enter()
			.append("path")
			.attr(
				"d",
				d3
					.arc()
					.innerRadius(innerRadius - innerThickness) // This is the size of the donut hole
					.outerRadius(innerRadius) as any
			)
			.attr("fill", (d) => timeNeutralColors[d.index]);
	}, [timeProgress]);

	useEffect(() => {
		if (initialLoad) return;
		drawOuterChart();
		if (showInner) drawInnerChart();
		// const interval = setInterval(() => {
		// 	setData((prev) => ({ ...prev, done: prev.done + 10 }));
		// }, 50);
		// return () => {
		// 	clearInterval(interval);
		// };
	}, [drawOuterChart, drawInnerChart, showInner, initialLoad]);

	if (initialLoad) return <div>Loading...</div>;

	return (
		<div className="list-widget">
			<div className="list-widget__heading">
				<div className="list-widget__title">{metricTypes[data.metricType]} Goal</div>
			</div>
			<div className="chart-container">
				<div className="donut-chart" ref={chartRef} style={{ width, height }}>
					<div
						className="goal-end"
						style={{
							transform: "translate(-20%)",
							width: thickness * 3,
							height: thickness,
							borderRadius: thickness / 2,
							backgroundColor: colors[Number(donePercent < 100)],
							color: donePercent < 100 ? "black" : "white",
							zIndex: donePercent > 50 ? 1 : 100,
						}}
					>
						{Number(target).toLocaleString()}
					</div>
					{donePercent < 100 && (
						<div
							className="progress-head"
							style={{
								height,
								width: thickness,
								transform: `translate(-50%) rotate(${deg}deg)`,
							}}
						>
							<div
								className="circle"
								style={{
									width: thickness - 1,
									height: thickness - 1,
									borderRadius: thickness / 2,
								}}
							></div>
						</div>
					)}
				</div>
				{showInner && (
					<div className="donut-chart" ref={innerChartRef} style={{ width: innerWidth, height: innerHeight }}>
						<div
							className="goal-end"
							style={{
								transform: "translate(-20%)",
								width: innerThickness * 3,
								height: innerThickness - 1,
								borderRadius: innerThickness / 2,
								backgroundColor: timeNeutralColors[Number(timePercent < 100)],
								color: timePercent < 100 ? "black" : "white",
								zIndex: timePercent > 50 ? 1 : 100,
							}}
						>
							{moment(iterationEndDate).format("HH:mm")}
						</div>
						{timePercent < 100 && (
							<div
								className="progress-head"
								style={{
									height: innerHeight,
									width: innerThickness,
									transform: `translate(-50%) rotate(${timeDeg}deg)`,
								}}
							>
								<div
									className="inner-circle"
									style={{
										width: innerThickness,
										height: innerThickness,
										borderRadius: innerThickness / 2,
									}}
								></div>
							</div>
						)}
					</div>
				)}
				<div className="donut-inside">
					<div className="done-metric">{data.metricType === 'totalReadingTime' ? Number(Math.round(data.achieved * 10) / 10).toLocaleString() : Number(achieved).toLocaleString()}</div>
					<div dangerouslySetInnerHTML={{ __html: timeLeftString }} />
				</div>
				<div className="current-progress">
					Progress {localeNumber((Number(achieved) / Number(target)) * 100)}% (
					{data.metricType === 'totalReadingTime' ? (Number(Math.round(data.achieved * 10) / 10).toLocaleString() + ' hrs') : Number(data.achieved).toLocaleString() + ' hits'})
				</div>
			</div>
		</div>
	);
}
